import z from 'zod';
import { request } from '../../utils/request';
import { StoreOfficialDocs } from '../../schemas/storeGetStartedSchema';
import uploadFile from '../fileUpload/uploadFile';
import { FilePurpose } from '../../libs/interfaces/filePurpose';

type ReqBody = {
  body: {
    role_id: number;
    file_id?: number;
    store_id: number;
    document_name: string;
    verification_type: string;
    metadata: {
      store_id: number;
      document_number?: string;
    };
  };
  authToken: string;
};
// THIS HANDLES UPDATING AND CREATION OF NEW STORE
const saveDocToBackend = (req: ReqBody) =>
  request({
    method: 'POST',
    url: `/auth/role-verification/documents`,
    headers: {
      Authorization: `Bearer ${req.authToken}`,
    },
    formData: req.body,
    onError: (err) => {
      throw err;
    },
  });

type saveStoreDocsReq = {
  body: z.infer<typeof StoreOfficialDocs>;
  store_id: number;
  authToken: string;
  role_id: number;
};

/// THIS IS THE MAIN UPLOAD MECHANISM FOR EACH FILE
const uploadCycle = async ({
  file_purpose,
  file,
  authToken,
  document_name,
  roleId,
  store_id,
  verification_type,
  document_number,
}: {
  authToken: string;
  file_purpose: FilePurpose;
  store_id: number;
  roleId: number;
  document_name: string;
  verification_type: string;
  document_number?: string;
  file?: File;
}) => {
  let file_id: any | undefined = undefined;
  let role_id: any | undefined = roleId;

  if (file) {
    const res = await uploadFile({
      file_purpose,
      file,
    });

    file_id = res.file_id;
    role_id = res.role_id;
  }

  await saveDocToBackend({
    body: {
      role_id,
      store_id,
      document_name,
      verification_type,
      file_id,
      metadata: {
        document_number,
        store_id,
      },
    },
    authToken,
  });
};

export const saveStoreOfficialDocs = async (req: saveStoreDocsReq) => {
  const {
    ADDRESS,
    CAC_NUMBER,
    CAC_CERT,
    CAC_STATUS_REPORT,
    PROOF_OF_ADDRESS,
    TAX_IDENTIFICATION_NUMBER,
    TIN_CERTIFICATE,
    MEMORANDUM_AND_ARTICLES,
    // DOC_NO_MNA,
  } = req.body;

  const docs = [
    {
      file_purpose: FilePurpose.CAC,
      document_name: 'CAC',
      document_number: CAC_NUMBER,
      verification_type: 'CAC',
      file: CAC_CERT,
    },
    {
      file_purpose: FilePurpose.CAC_STATUS_REPORT,
      document_name: 'CAC_STATUS_REPORT',
      verification_type: 'CAC_STATUS_REPORT',
      file: CAC_STATUS_REPORT,
    },
    {
      file_purpose: FilePurpose.PROOF_OF_ADDRESS,
      document_name: 'PROOF_OF_ADDRESS',
      document_number: ADDRESS,
      verification_type: 'PROOF_OF_ADDRESS',
      file: PROOF_OF_ADDRESS,
    },
    {
      file_purpose: FilePurpose.TAX_IDENTIFICATION_NUMBER,
      document_name: 'TAX_IDENTIFICATION_NUMBER',
      document_number: TAX_IDENTIFICATION_NUMBER,
      verification_type: 'TAX_IDENTIFICATION_NUMBER',
      file: TIN_CERTIFICATE,
    },
    {
      file_purpose: FilePurpose.MEMORANDUM_AND_ARTICLES,
      document_name: 'MEMORANDUM_AND_ARTICLES',
      verification_type: 'MEMORANDUM_AND_ARTICLES',
      file: MEMORANDUM_AND_ARTICLES,
    },
  ];

  // Only submit docs the user has actually staged in this session. A doc is
  // submittable if it has a freshly staged file OR a document number on its
  // own (e.g. TIN, where the certificate is optional) — anything with neither
  // is either already saved on the backend or untouched.
  const hasDocNumber = (value?: string) =>
    typeof value === 'string' && value.trim().length > 0;
  const res = await Promise.all(
    docs
      .filter((d) => d.file instanceof File || hasDocNumber(d.document_number))
      .map(
        ({
          document_name,
          document_number,
          file_purpose,
          verification_type,
          file,
        }) =>
          uploadCycle({
            authToken: req.authToken,
            file_purpose: file_purpose,
            store_id: req.store_id,
            roleId: req.role_id,
            document_name,
            document_number: document_number ?? undefined,
            verification_type,
            file,
          })
      )
  );

  return res;
};
